import React, { useState } from 'react';
import { getImageUrl } from '../../services/imageService';
import './CampaignPreview.css';

const PLACEMENT_LABELS = {
  hero: 'Hero slider',
  strip: 'Promo strip',
  wide: 'Wide banner',
  section: 'Product section',
};

const getThumb = (product) => {
  const image =
    product?.images?.find((img) => img.is_primary) || product?.images?.[0];
  return image?.image_url ? getImageUrl(image.image_url) : null;
};

/**
 * Live preview of how the campaign will look on the storefront.
 *
 * This deliberately mirrors the storefront's CampaignMedia markup and styles
 * rather than importing it - the two apps are separate builds. Keep the two
 * in step when either changes.
 */
const CampaignPreview = ({
  formData,
  desktopImage,
  mobileImage,
  products = [],
  selectedIds = [],
}) => {
  const [device, setDevice] = useState('desktop');

  const placement = formData.placement || 'strip';
  const theme = formData.text_theme === 'dark' ? 'dark' : 'light';
  const position = formData.text_position || 'left';
  const isMobile = device === 'mobile';

  // Mobile falls back to the desktop art, exactly as the storefront does
  const art = isMobile ? mobileImage || desktopImage : desktopImage;

  const selected = selectedIds
    .map((id) => products.find((p) => p.id === id))
    .filter(Boolean);

  const renderArt = () => (
    <div
      className={`cp-media cp-media--${theme} cp-media--${position}`}
      style={{ '--cp-scrim': (formData.overlay_opacity ?? 35) / 100 }}
    >
      {art ? (
        <img src={art} alt="" className="cp-media-img" />
      ) : (
        <div className="cp-media-empty">
          Upload a {isMobile && !mobileImage ? 'desktop' : device} image
        </div>
      )}
      <div className="cp-media-scrim" />
      <div className="cp-media-content">
        {formData.kicker && <span className="cp-kicker">{formData.kicker}</span>}
        <span className="cp-title">{formData.title || 'Campaign title'}</span>
        {formData.subtitle && <p className="cp-subtitle">{formData.subtitle}</p>}
        {formData.cta_text && (
          <span className="cp-cta">{formData.cta_text}</span>
        )}
      </div>
    </div>
  );

  // A 'section' campaign shows its products inline instead of art with copy
  const renderSection = () => (
    <div className="cp-section">
      <div className="cp-section-head">
        <div>
          {formData.kicker && (
            <span className="cp-section-kicker">{formData.kicker}</span>
          )}
          <h4 className="cp-section-title">
            {formData.title || 'Campaign title'}
          </h4>
          {formData.subtitle && (
            <p className="cp-section-sub">{formData.subtitle}</p>
          )}
        </div>
        <span className="cp-section-link">View all</span>
      </div>
      <div className="cp-section-grid">
        {selected.length === 0 && (
          <div className="cp-section-placeholder">
            Pick products to see them here.
          </div>
        )}
        {selected.slice(0, isMobile ? 2 : 4).map((product) => {
          const thumb = getThumb(product);
          return (
            <div key={product.id} className="cp-card">
              {thumb ? (
                <img src={thumb} alt="" className="cp-card-img" />
              ) : (
                <div className="cp-card-img cp-card-img--empty" />
              )}
              <span className="cp-card-name">{product.name}</span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="campaign-preview">
      <div className="cp-bar">
        <span className="cp-bar-label">
          Preview
          <span className="cp-bar-placement">
            {PLACEMENT_LABELS[placement] || placement}
          </span>
        </span>
        <div className="cp-device-toggle">
          <button
            type="button"
            className={device === 'desktop' ? 'is-active' : ''}
            onClick={() => setDevice('desktop')}
          >
            Desktop
          </button>
          <button
            type="button"
            className={device === 'mobile' ? 'is-active' : ''}
            onClick={() => setDevice('mobile')}
          >
            Mobile
          </button>
        </div>
      </div>

      <div className={`cp-stage cp-stage--${device}`}>
        <div className={`cp-frame cp-frame--${placement}`}>
          {placement === 'section' ? renderSection() : renderArt()}
        </div>
      </div>

      {isMobile && !mobileImage && desktopImage && (
        <p className="cp-note">
          No mobile image uploaded - the desktop image is being used.
        </p>
      )}
    </div>
  );
};

export default CampaignPreview;
